import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { Building2 } from "lucide-react";
import { CompaniesItems } from "@vviinn/user-managment-client-ts";

import { useApi } from "../../api";
import {
  useAppDispatch,
  useAppSelector,
  getCompany,
  fetchCompanies,
} from "../../store";
import {
  ListWrapper,
  ListSettings,
  ListFilter,
} from "../../ui/layout/listPages";
import { DetailedCard } from "../../components/GeneralUI/DetailedCardComponents";
import { useAuth } from "../../auth";

import { CompanyFilters } from "../../company";
import { AppearedContent } from "../../components/DataDisplay/AppearedContent";
import { NA_MESSAGE } from "../../ui/constants";

const LIST_TITLE = "Companies";
const SINGLE_ITEM_TITLE = "company";

const filteringOptions: ListFilter<CompanyFilters, CompaniesItems>[] = [
  [
    {
      text: "Any Contract",
      rule: () => true,
    },
    {
      text: "Active Contract",
      rule: (company) => !!company["hasActiveContract"],
    },
    {
      text: "No Active Contract",
      rule: (company) => !company["hasActiveContract"],
    },
  ],
  [
    {
      text: "Any Plan",
      rule: () => true,
    },
    {
      text: "Valid Plan",
      rule: (company) => !!company["hasValidPlan"],
    },
    {
      text: "No Plan",
      rule: (company) => !company.plan,
    },
    {
      text: "Expired Plan",
      rule: (company) =>
        company.plan?.endDate
          ? new Date(company.plan.endDate) < new Date()
          : false,
    },
  ],
];

const selectCompanies = (
  companies: CompaniesItems[],
  search: string,
  filters: CompanyFilters[],
) => {
  const lowercaseSearch = search.toLowerCase();
  let formattedCompanies = [...companies];
  if (search) {
    formattedCompanies = formattedCompanies.filter((company) =>
      company?.name?.toLowerCase().includes(lowercaseSearch),
    );
  }
  if (filters.length > 0) {
    const relevantFilterOptions = filteringOptions
      .flat()
      .filter(({ text }) => filters.includes(text));
    formattedCompanies = formattedCompanies.filter((company) =>
      relevantFilterOptions.reduce(
        (accumulator, currentFilter) =>
          accumulator && currentFilter.rule(company),
        true,
      ),
    );
  }
  return formattedCompanies;
};

const setIconColors = (hasActiveContract?: boolean, hasValidPlan?: boolean) =>
  hasActiveContract
    ? hasValidPlan
      ? "bg-green-500 text-green-100 "
      : "bg-red-500 text-white"
    : hasValidPlan
      ? "bg-amber-500 text-white"
      : "bg-stone-300 text-white dark:bg-stone-600";

export const Companies: FC<React.PropsWithChildren> = () => {
  const { companies, loading } = useAppSelector((state) => state.companies);
  const { selectedFilters } = useAppSelector((state) => state.ui.companies);
  const { user } = useAuth();

  const [search, setSearch] = useState("");
  const dispatch = useAppDispatch();
  const { getApi } = useApi();
  const navigate = useNavigate();

  const currentCompanies = selectCompanies(companies, search, selectedFilters);

  const updateCompanies = async () => {
    if (!user) return;
    const api = await getApi();
    await dispatch(fetchCompanies(api));
  };

  useEffect(() => {
    if (companies && companies.length > 0) updateCompanies();
  }, [user]);

  return (
    <ListWrapper
      title={LIST_TITLE}
      newItemTitle={SINGLE_ITEM_TITLE}
      createPermissions="companies:companies:create"
    >
      <AppearedContent isLoaded={!loading} skeletonOptions={{ amount: 1 }}>
        {companies.length > 0 && (
          <ListSettings
            title={LIST_TITLE.toLowerCase()}
            quantity={currentCompanies.length}
            initSearch={search}
            searchMessage="by title"
            handleSearch={setSearch}
            activeFilters={selectedFilters}
            feature="companies"
            listFilters={filteringOptions}
          />
        )}

        {companies.length > 0 && (
          <div className="grid grid-cols-1 gap-4 sm:gap-5 lg:grid-cols-2">
            {currentCompanies.map(
              ({ name, sites, id, hasActiveContract, hasValidPlan, plan }) => (
                <DetailedCard
                  key={id}
                  onClick={async () => {
                    if (!id) return;
                    const api = await getApi();
                    await dispatch(getCompany({ api, value: id }));
                    navigate(`/company/${id}/`);
                  }}
                  icon={<Building2 />}
                  title={name}
                  subtitles={[
                    {
                      type: "object",
                      items: [
                        {
                          title: "Sites",
                          value: sites
                            ? sites.current + " / " + sites?.limit
                            : "0 / 0",
                        },
                      ],
                    },
                    {
                      type: "object",
                      items: [
                        {
                          title: "Plan",
                          value: plan
                            ? `${plan.startDate}
                              ${plan.endDate ? ` / ${plan.endDate}` : ""}`
                            : NA_MESSAGE,
                        },
                      ],
                    },
                  ]}
                  specialIconStyle={setIconColors(
                    hasActiveContract,
                    hasValidPlan,
                  )}
                  isExtended={false}
                ></DetailedCard>
              ),
            )}
          </div>
        )}
      </AppearedContent>
    </ListWrapper>
  );
};
